import { PropsWithChildren, ReactElement, Ref, forwardRef } from 'react';
import { Platform, Pressable, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { TIconProps } from '@/common/components/Icons/Icon';
import { IconCoverflex } from '@/common/components/Icons/custom/IconCoverflex';
import { Label } from '@/common/components/Label/Label';
import { LayoutConformance } from '@/common/components/LayoutConformance/LayoutConformance';
import { RouterLink } from '@/common/components/RouterLink/RouterLink';
import { Stack } from '@/common/components/Stack/Stack';
import { ROUTES } from '@/common/constants/routes';
import { useEmployeeDetails } from '@/common/contexts/user/UserDetailsContext';
import { useBreakpoints } from '@/common/hooks/useBreakpoints';
import { StringUtils } from '@/common/utils/string';
import { TabTrigger, TabTriggerSlotProps } from 'expo-router/ui';
import { styles } from './TabBar.styles';
import { getIconColor } from './TabBar.utils';

export const TabBar = forwardRef<View, PropsWithChildren>((props, ref) => {
  const { bottom } = useSafeAreaInsets();
  const { employee, company } = useEmployeeDetails();
  return (
    <View
      ref={ref}
      className={styles.root}
      style={{
        paddingBottom: bottom,
      }}
    >
      <UserLink>
        <Stack gap="xs" className={styles.user}>
          <Label size="sm" bold>
            {StringUtils.getFirstLastName(employee.name)}
          </Label>
          <Label size="sm">
            {StringUtils.truncateString(company.name, 28)}
          </Label>
        </Stack>
      </UserLink>
      <Stack className={styles.nav}>{props.children}</Stack>
      <View className={styles.logo}>
        <IconCoverflex />
      </View>
    </View>
  );
});
TabBar.displayName = 'TabBar';

const UserLink = ({ children }: PropsWithChildren) => {
  if (Platform.OS === 'web') {
    return (
      <RouterLink href={ROUTES.PROFILE} className={styles.userLink} dismissTo>
        {children}
      </RouterLink>
    );
  }
  return (
    <TabTrigger name={ROUTES.PROFILE} className={styles.userLink}>
      {children}
    </TabTrigger>
  );
};

export const TabBarTrigger = forwardRef(
  (
    {
      icon,
      label,
      children,
      isFocused,
      notification,
      ...props
    }: TTabBarTriggerProps,
    ref: Ref<View>
  ) => {
    const breakpoint = useBreakpoints(['lg']);
    const iconColor = getIconColor(isFocused, breakpoint.lg);
    return (
      <LayoutConformance mode="strict">
        <Pressable ref={ref} {...props} style={{}} className={styles.trigger}>
          {isFocused && <View className={styles.triggerDot} />}
          <icon.type {...icon.props} color={iconColor} />
          <Label
            className={styles.triggerLabel({
              focused: isFocused,
            })}
          >
            {label}
          </Label>
          {notification && <View className={styles.notification} />}
        </Pressable>
      </LayoutConformance>
    );
  }
);

TabBarTrigger.displayName = 'TabBarTrigger';

export type TTabBarTriggerProps = TabTriggerSlotProps & {
  icon: ReactElement<TIconProps>;
  label: string;
  hidden?: boolean;
  notification?: boolean;
};
